/*
 * RelatedItemsManager.tsx : Un composant pour gérer les éléments liés et non liés.
 * Pour l'utiliser, on appelle le composant en lui passant les éléments liés
 * Il faut toutefois penser à ajouter les business rules dans l'API
 */

import React from 'react';
import { faLink, faUnlink } from '@fortawesome/free-solid-svg-icons';
import {
	Column,
	FooterTotal,
} from 'common/components/RelatedItemsTable/RelatedItemsTable';
import RelatedItemsTable from 'common/components/RelatedItemsTable/RelatedItemsTable';

interface RelatedItemsManagerProps<T extends { id: number }> {
	linkedItems: T[];
	setLinkedItems: (items: T[]) => void;
	fetchAll: () => Promise<T[]>;
	isLinked: (item: T) => boolean;
	columns: Column<T>[];
	linkedTitle?: string;
	unlinkedTitle?: string;
	readOnly?: boolean;
	readOnlyTooltip?: string;
	idUrlPrefix?: string;
	/** Totaux affichés en pied du tableau des éléments liés (optionnel). */
	linkedFooterTotals?: FooterTotal<T>[];
}

function RelatedItemsManager<T extends { id: number }>({
	linkedItems,
	setLinkedItems,
	fetchAll,
	isLinked,
	columns,
	linkedTitle = 'Éléments liés',
	unlinkedTitle = 'Éléments non liés',
	readOnly = false,
	readOnlyTooltip,
	idUrlPrefix,
	linkedFooterTotals,
}: RelatedItemsManagerProps<T>) {
	const [loading, setLoading] = React.useState(true);
	const [unlinkedItems, setUnlinkedItems] = React.useState<T[]>([]);

	const linkAll = () => {
		setLinkedItems([...linkedItems, ...unlinkedItems]);
		setUnlinkedItems([]);
	};

	React.useEffect(() => {
		fetchAll()
			.then((all) => {
				setUnlinkedItems(all.filter((i) => !isLinked(i)));
			})
			.finally(() => setLoading(false));
	}, []);

	const link = (item: T) => {
		setLinkedItems([...linkedItems, item]);
		setUnlinkedItems(unlinkedItems.filter((i) => i.id !== item.id));
	};

	const unlink = (item: T) => {
		setLinkedItems(linkedItems.filter((i) => i.id !== item.id));
		setUnlinkedItems([...unlinkedItems, item]);
	};

	if (loading) return null;

	return (
		<>
			<RelatedItemsTable
				title={linkedTitle}
				items={linkedItems}
				columns={columns}
				actionIcon={faUnlink}
				actionColorClass="text-red-500"
				onAction={unlink}
				headerActionIcon={faLink}
				onHeaderAction={linkAll}
				emptyLabel="Aucun élément lié."
				readOnly={readOnly}
				readOnlyTooltip={readOnlyTooltip}
				idUrlPrefix={idUrlPrefix}
				footerTotals={linkedFooterTotals}
			/>

			{!readOnly && (
				<RelatedItemsTable
					title={unlinkedTitle}
					items={unlinkedItems}
					columns={columns}
					actionIcon={faLink}
					actionColorClass="text-green-500"
					onAction={link}
					emptyLabel="Aucun élément disponible."
					collapsed={true}
					showCount={true}
					readOnly={readOnly}
					idUrlPrefix={idUrlPrefix}
				/>
			)}
		</>
	);
}
export default RelatedItemsManager;
